import React, { useEffect, useState, useRef } from 'react';
import { UserLayout } from '../../components/layout/UserLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { LifeBuoy, Plus, MessageCircle, X, Send, ArrowLeft, Circle } from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'swap', label: 'Swap' },
  { value: 'voucher', label: 'Voucher' },
  { value: 'shop', label: 'Shop' },
  { value: 'predictions', label: 'Predictions' },
  { value: 'account', label: 'Account' },
  { value: 'bug', label: 'Bug Report' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_STYLES = {
  open: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  in_progress: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
};

const PRIORITY_STYLES = {
  urgent: 'bg-red-500/15 text-red-600 border-red-500/30',
  high: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  normal: 'bg-muted text-muted-foreground border-border',
  low: 'bg-muted text-muted-foreground border-border',
};

export const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'normal', message: '' });
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadTickets(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await userAPI.listTickets();
      setTickets(res.data);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally { setLoading(false); }
  };

  const openTicket = async (id) => {
    try {
      const res = await userAPI.getTicket(id);
      setSelectedTicket(res.data.ticket);
      setMessages(res.data.messages);
      loadTickets();
    } catch (e) {
      toast.error('Failed to open ticket');
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 3) return toast.error('Subject too short');
    if (form.message.trim().length < 5) return toast.error('Message too short');
    setCreating(true);
    try {
      const res = await userAPI.createTicket(form);
      toast.success('Ticket created');
      setShowCreate(false);
      setForm({ subject: '', category: 'general', priority: 'normal', message: '' });
      await loadTickets();
      openTicket(res.data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed to create');
    } finally { setCreating(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await userAPI.replyTicket(selectedTicket.id, reply.trim());
      setReply('');
      const res = await userAPI.getTicket(selectedTicket.id);
      setSelectedTicket(res.data.ticket);
      setMessages(res.data.messages);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed to send');
    } finally { setSending(false); }
  };

  const closeTicket = async () => {
    if (!window.confirm('Close this ticket? You can always open a new one.')) return;
    try {
      await userAPI.closeTicket(selectedTicket.id);
      toast.success('Ticket closed');
      setSelectedTicket(null);
      setMessages([]);
      loadTickets();
    } catch (e) {
      toast.error('Failed to close');
    }
  };

  if (selectedTicket) {
    return (
      <UserLayout>
        <div className="space-y-4" data-testid="support-thread">
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); setMessages([]); }} data-testid="back-to-tickets">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            {selectedTicket.status !== 'closed' && (
              <Button variant="outline" size="sm" onClick={closeTicket} data-testid="close-ticket-btn">
                <X className="h-4 w-4 mr-2" /> Close ticket
              </Button>
            )}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle data-testid="ticket-subject">{selectedTicket.subject}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Opened {formatDate(selectedTicket.created_at)} · {selectedTicket.category}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className={STATUS_STYLES[selectedTicket.status]}>{selectedTicket.status.replace('_', ' ')}</Badge>
                  <Badge variant="outline" className={PRIORITY_STYLES[selectedTicket.priority]}>{selectedTicket.priority}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      m.sender_role === 'admin'
                        ? 'bg-blue-500/10 border border-blue-500/20'
                        : 'bg-muted'
                    }`} data-testid={`msg-${m.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-medium">
                          {m.sender_role === 'admin' ? 'Matrise Support' : m.sender_name || 'You'}
                        </p>
                        <span className="text-[10px] text-muted-foreground">{formatDate(m.created_at)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {selectedTicket.status !== 'closed' ? (
                <div className="mt-4 border-t pt-4 space-y-2">
                  <Textarea
                    placeholder="Type your reply..."
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    data-testid="reply-input"
                  />
                  <div className="flex justify-end">
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="send-reply-btn">
                      <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 border-t pt-4 text-center text-sm text-muted-foreground">
                  This ticket is closed. Open a new ticket if you need further help.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6" data-testid="support-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Support</h1>
            <p className="mt-2 text-muted-foreground">Get help from the Matrise team. We usually reply within a few hours.</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-2" /> New Ticket
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5" /> Your Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading...</p>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground opacity-40" />
                <p className="mt-3 text-muted-foreground">No tickets yet. Click &quot;New Ticket&quot; if you need help.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t.id)}
                    className="w-full text-left p-4 rounded-lg border border-border hover:bg-muted/60 transition-colors"
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {t.unread_by_user && <Circle className="h-2 w-2 fill-blue-500 text-blue-500 shrink-0" />}
                          <p className="font-medium truncate">{t.subject}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t.category} · {t.message_count} message{t.message_count !== 1 ? 's' : ''} · Updated {formatDate(t.updated_at)}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Badge variant="outline" className={STATUS_STYLES[t.status]}>{t.status.replace('_', ' ')}</Badge>
                        <Badge variant="outline" className={PRIORITY_STYLES[t.priority]}>{t.priority}</Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Support Ticket</DialogTitle>
            <DialogDescription>Tell us what&apos;s going on. Our support team usually replies within a few hours.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Briefly describe your issue"
                maxLength={200}
                data-testid="new-subject-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="new-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="new-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Describe your issue in detail..."
                maxLength={5000}
                data-testid="new-message-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="submit-ticket-btn">
                {creating ? 'Creating...' : 'Submit Ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};
